/**
 * @module experience/one-time-payment
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
import { CreditCardIngenicoResponse } from '@support/ingenico/interfaces/ingenico.interface';
import { Token } from '@support/api/proxies/requests/api-372.interface';
import { CreditCardInfineaResponse } from '@support/infinea/interfaces/infinea.interface';
import {
  CARD_TYPE,
  SHARED_PAYMENT,
} from '@support/payment/shared-payment/constants/shared-payment.constants';

export class InStoreCardDetails {
  /**
   * Type of card
   */
  public typeCode: string;
  /**
   * authorization Id
   */
  public authorizationId?: string;
  /**
   * Card last 4 number
   */
  public cardLast4: string;
  /**
   * Name on Card
   */
  public nameOnCard: string;
  /**
   * Card Number/ Account Number
   */
  public cardNumber: string;
  /**
   * Card expiration month
   */
  public expirationMonth: string;
  /**
   * Card expiration year
   */
  public expirationYear: string;
  /**
   *  Terminal Entry Capability
   */
  public terminalEntryCapability: string;
  /**
   *  Terminal Type
   */
  public terminalType: string;
  /**
   *  Terminal Entry Mode
   */
  public terminalEntryMode: string;
  /**
   *  Electronic Authentication Capability
   */
  public electronicAuthenticationCapability: string;
  /**
   * CVM Mode
   */
  public cvm: string;
  /**
   *  Token
   */
  public token: Token;
  /**
   *  Track2data
   */
  public track2data: string;
  /**
   *  card Type Indicator
   */
  public cardTypeIndicator: string;
  /**
   *  Method
   */
  public method: string;
  /**
   * Payment Method Code
   */
  public paymentMethodCode?: string;
  /**
   *  Encryption Target
   */
  public encryptionTarget: string;
  /**
   * Customer's signature
   */
  public signature: string;
  /**
   * flag that indicates if P2P encryption is on/off
   */
  public isEncryptionOn: boolean;
  /**
   * Pin number if debit
   */
  public PIN: string;
  /**
   * paymentId
   */
  public paymentId: number;
  /**
   * chargeAccountNumberIndicator
   */
  public chargeAccountNumberIndicator: string;
  /**
   * amount
   */
  public amount: string;
  /**
   * chip application id
   */
  public aid: string;
  /**
   * applicationCryptogram
   */
  public applicationCryptogram: string;
  /**
   * Card name
   */
  public applicationName: string;
  /**
   * boolean is card inserted
   */
  public isCardInserted: boolean;
  /**
   * boolean is credit card
   */
  public isCreditCard: boolean;
  /**
   * string integratedChipData
   */
  public de55: string;
  /**
   * To check the cardReadSource
   */
  public cardReadSource: string;
  constructor() {
    this.token = {
      encryptionTarget: '',
      encryptedContent: '',
    };
  }

  /**
   *
   * @param {CreditCardIngenicoResponse} ingenicoCardData
   */
  public setIngenicoData(ingenicoCardData: CreditCardIngenicoResponse): void {
    this.token = {
      encryptionTarget: '',
      encryptedContent: '',
    };
    if (ingenicoCardData) {
      const track: number = 2;
      this.cardReadSource = ingenicoCardData.cardReadSource;
      this.nameOnCard = ingenicoCardData.cardholderName;
      this.cardNumber = ingenicoCardData.accountNumber;
      this.expirationMonth = ingenicoCardData.expirationMonth;
      this.terminalType = track.toString();
      this.expirationYear =
        SHARED_PAYMENT.DEFAULT_YEAR_CENTURY +
        ingenicoCardData.expirationYear.substring(ingenicoCardData.expirationYear.length - track);
      this.terminalEntryCapability = ingenicoCardData.TerminalCapability;
      this.cvm = ingenicoCardData.cvm;
      this.terminalEntryMode = ingenicoCardData.TerminalEntryMode;
      this.electronicAuthenticationCapability = ingenicoCardData.PinAuthorizationCapability;
      this.token.encryptedContent = ingenicoCardData.tracks ? ingenicoCardData.tracks[track] : '';
      this.track2data = this.token.encryptedContent || ingenicoCardData.tracks[1];
      this.token.encryptionTarget = ingenicoCardData.encryptionTarget;
      this.encryptionTarget = ingenicoCardData.encryptionTarget;
      this.PIN = ingenicoCardData.pinBlock;
      this.cardTypeIndicator = ingenicoCardData.CardTypeIndicator;
      this.isEncryptionOn = ingenicoCardData.isEncryptionOn || !!this.token.encryptedContent;
      this.typeCode = CARD_TYPE[ingenicoCardData.cardType];
      this.isCardInserted = ingenicoCardData.isCardInserted;
      this.aid = ingenicoCardData.aid;
      this.applicationCryptogram = ingenicoCardData.applicationCryptogram;
      this.applicationName = ingenicoCardData.applicationName
        ? ingenicoCardData.applicationName.replace(/ /g, '')
        : '';
      this.de55 = ingenicoCardData.de55;
      this.isCreditCard = ingenicoCardData.isCreditCard;
      this.typeCode = CARD_TYPE[ingenicoCardData.cardType];
      this.paymentMethodCode =
        !ingenicoCardData.isCreditCard ||
        (ingenicoCardData.cardType &&
          ingenicoCardData.cardType.toLocaleUpperCase() === SHARED_PAYMENT.DEBIT_CARD_TYPE)
          ? SHARED_PAYMENT.DEBIT
          : SHARED_PAYMENT.CREDIT;
    } else {
      this.reset();
    }
  }

  /**
   *
   * @param {CreditCardInfineaResponse} infineaCardData
   */
  public setInfineaData(infineaCardData: CreditCardInfineaResponse): void {
    this.token = {
      encryptionTarget: '',
      encryptedContent: '',
    };
    if (infineaCardData) {
      const track: number = 2;
      this.cardReadSource = infineaCardData.cardReadSource;
      this.nameOnCard = infineaCardData.cardholderName;
      this.cardNumber = infineaCardData.accountNumber;
      this.expirationMonth = infineaCardData.expirationMonth;
      this.terminalType = track.toString();
      this.expirationYear =
        SHARED_PAYMENT.DEFAULT_YEAR_CENTURY +
        infineaCardData.expirationYear.substring(infineaCardData.expirationYear.length - track);
      this.terminalEntryCapability = infineaCardData.terminalEntryCapability;
      this.terminalEntryMode = infineaCardData.terminalEntryMode;
      this.PIN = infineaCardData.debitPinBlock;
      this.cvm = infineaCardData.cvm;
      this.electronicAuthenticationCapability = infineaCardData.PinAuthorizationCapability;
      this.token.encryptedContent = this.track2data = infineaCardData.tracks
        ? infineaCardData.tracks[track]
        : '';
      this.token.encryptionTarget = infineaCardData.encryptionTarget;
      this.encryptionTarget = infineaCardData.encryptionTarget;
      this.cardTypeIndicator = infineaCardData.cardTypeIndicator;
      this.isEncryptionOn = infineaCardData.isEncryptionOn || !!this.token.encryptedContent;
      this.isCardInserted = infineaCardData.isCardInserted;
      this.aid = infineaCardData.aid;
      this.applicationCryptogram = infineaCardData.applicationCryptogram;
      this.applicationName = infineaCardData.applicationPreferredName
        ? infineaCardData.applicationPreferredName.replace(/ /g, '')
        : '';
      this.de55 = infineaCardData.de55;
      this.isCreditCard = infineaCardData.isCreditCard;
      this.typeCode = CARD_TYPE[infineaCardData.cardType];
      this.paymentMethodCode =
        infineaCardData.cardType &&
        infineaCardData.cardType.toLocaleUpperCase() === SHARED_PAYMENT.DEBIT_CARD_TYPE
          ? SHARED_PAYMENT.DEBIT
          : SHARED_PAYMENT.CREDIT;
    } else {
      this.reset();
    }
  }

  /**
   * Set the payment method
   * @param {number} paymentId
   */
  public setPaymentId(paymentId: number): void {
    this.paymentId = paymentId;
  }

  /**
   * Function to clear the data if transaction was canceled.
   */
  public reset(): void {
    this.typeCode = '';
    this.nameOnCard = '';
    this.cardNumber = '';
    this.expirationMonth = '';
    this.expirationYear = '';
    this.terminalEntryCapability = '';
    this.terminalEntryMode = '';
    this.cvm = '';
    this.electronicAuthenticationCapability = '';
    this.token.encryptedContent = this.track2data = '';
    this.token.encryptionTarget = '';
    this.encryptionTarget = '';
    this.isEncryptionOn = false;
    this.cardTypeIndicator = '';
    this.isCardInserted = false;
    this.aid = '';
    this.signature = '';
    this.PIN = '';
    this.applicationCryptogram = '';
    this.applicationName = '';
    this.de55 = '';
  }
}
